import axios, { AxiosError, AxiosProgressEvent, AxiosResponse, HttpStatusCode } from "axios"
import { useSessionStore } from '@/store/session'

import { PropertyValueDTO } from "@/store/classification"
import { LocalizationDTO } from "@/store/localization"
import { DEBUG, DEBUGC, DEBUG_ERR_WITHOUT_PROD_NOTIFICATION, DEBUG_WARN, NumDictionary } from ".."
import { NotificationDTO, useNotificationStore } from '@/store/notification'

import { IEquipmentMasterDataWithBomWarningCount, IEquipmentDataWithBomItemWarnings, IEquipmentMasterDataDTO, IEquipmentMasterDataWithDocCountDTO } from '@/types/equipment'
import { ISyncEventDTO } from '@/types/ISyncEventDTO'
import { ICountryDTO } from '@/types/country'
import { IDeliveryAddressDTO, IOrderingData, IShoppingCartDTO } from '@/types/shoppingCart'
import { IOrderProposalDTO, ICreateOrderProposalRequest, IExtendOrderProposalRequest, IAddToCartRequest } from '@/types/shoppingCart'
import { IBomDeviationDTO, IBomHeaderDTO, IBomItemQtyDTO } from '@/types/bom'
import { IMaterialDTO } from '@/types/material'
import { IDocumentDTO } from '@/types/document'
import { IPartnerAccountFullDTO } from '@/types/partnerAccount'
import { IPermissionGrant, IUserAccountInfoDTO } from '@/types/userAccount'
import { ICredentialsDTO, ILoginResultDTO, IPasswordResetResultDTO, IPasswordResetDTO} from '@/types/auth'
import { IVarConfItemDTO } from '@/types/equipment'
import { IPortalMessage } from '@/types/portalMessage'
import { IUnitDTO } from '@/types/IUnitDTO'
import { IContactSupportRequestDTO } from '@/types/contactSupport'
import { IRequestAccountDTO } from '@/types/requestAccount'

type KeyValuePair = {
    key: string,
    value: string
}

/********************************************************************************************************************
 UrlBuilder
 ********************************************************************************************************************/
class UrlBuilder {
    private _baseUrl: string
    private _parameters: KeyValuePair[]

    constructor(baseUrl: string) {
        this._baseUrl = baseUrl
        this._parameters = []
    }

    withParameter(key: string, value: any): UrlBuilder {
        this._parameters.push({ key, value: value.toString() })
        return this
    }

    withParameters(parms: { [key: string]: any }) {
        for (const key in parms) {
            this.withParameter(key, parms[key])
        }

        return this
    }

    withSegment(segment: string) {
        if (!this._baseUrl.endsWith("/")) {
            this._baseUrl += "/"
        }

        this._baseUrl += segment

        return this
    }

    build(methodName?: string): string {
        let result = methodName == null
            ? `/api/${this._baseUrl}`
            : `/api/${this._baseUrl}/${methodName}`

        if (this._parameters.length) {
            const paramStrings = this._parameters.map(kv => `${kv.key}=${kv.value}`)

            result += `?${paramStrings.join('&')}`
        }

        return result
    }
}

/********************************************************************************************************************
 ApiBase
 ********************************************************************************************************************/

// Automatic retry parameters for transient server errors (500/502/503/504) on GET requests
const RETRY_MAX = 3
const RETRY_INITIAL_DELAY_MS = 2000
const RETRY_MAX_DELAY_MS = 5000

// Throttle production error toasts to avoid spamming the user
const ERROR_TOAST_THROTTLE_MS = 30000
let _lastErrorToastTime = 0

/** Shows a throttled, production-visible error toast when the server is unreachable or returns a fatal error */
function showThrottledErrorToast(notificationStore: ReturnType<typeof useNotificationStore>) {
    const now = Date.now()
    if (now - _lastErrorToastTime >= ERROR_TOAST_THROTTLE_MS) {
        _lastErrorToastTime = now
        notificationStore.addNotification(
            new NotificationDTO('The server is currently unavailable. Please try again later.', 'error', 15000)
        )
    }
}

class ApiBase {
    private static _injectionInPlace = false;

    private _baseUrl: string

    constructor(baseUrl: string) {
        this._baseUrl = baseUrl

        if (!ApiBase._injectionInPlace) {
            ApiBase.setInjections()
        }
    }

    protected urlBuilder(): UrlBuilder {
        return new UrlBuilder(this._baseUrl)
    }

    private static setInjections() {
        const sessionStore = useSessionStore()
        DEBUG('Setting AXIOS injection...')

        axios.interceptors.request.clear()
        axios.interceptors.response.clear()

        const notificationStore = useNotificationStore()

        // Inject bearer token into each subsequent axios request
        axios.interceptors.request.use(
            async (config) => {

                DEBUGC(`REQUEST: ${config.url}`, 'yellow')
                notificationStore.addNotification(NotificationDTO.createForDebug(`REQUEST: ${config.url}`, 'info'))

                if (config.url != '/api/authentication/refreshToken') {
                    // Ensure valid token (could have expired while being on a page)
                    const token = await sessionStore.getValidToken()

                    // Now set the token
                    if (token != null) {
                        config.headers['authorization'] = token
                    }
                }

                return config
            },
            (error) => {
                DEBUG_ERR_WITHOUT_PROD_NOTIFICATION('REQUEST ERROR', error)
                return Promise.reject(error)
            }
        )

        axios.interceptors.response.use(
            response => {
                DEBUG(`RESPONSE (${response.config.url}):`, response?.data, response)
                return response
            },
            (error: AxiosError) => {
                const config = error.config
                const status = error.response?.status
                const isTransient = status != null && [500, 502, 503, 504].includes(status)
                const isNetworkError = !error.response && error.code === 'ERR_NETWORK'

                if (config && config.method === 'get' && (isTransient || isNetworkError)) {
                    const retryCount = ((config as any).__retryCount ?? 0) + 1

                    if (retryCount <= RETRY_MAX) {
                        const delay = Math.min(RETRY_INITIAL_DELAY_MS * Math.pow(1.5, retryCount - 1), RETRY_MAX_DELAY_MS)
                        DEBUGC(`[Retry] ${config.url} (${status ?? error.code}) attempt ${retryCount}/${RETRY_MAX} in ${Math.round(delay)} ms`, 'orange')

                        return new Promise<AxiosResponse>((resolve, reject) => {
                            setTimeout(() => {
                                (config as any).__retryCount = retryCount
                                axios.request(config).then(resolve, reject)
                            }, delay)
                        })
                    }
                }

                if (status == HttpStatusCode.NotFound) {
                    DEBUG_WARN('RESPONSE ERROR', error, error.response?.statusText)
                }
                else if (status == HttpStatusCode.Unauthorized) {
                    DEBUG_WARN('RESPONSE ERROR - UNAUTHORIZED', error, error.response?.statusText)
                }
                else {
                    DEBUG_ERR_WITHOUT_PROD_NOTIFICATION('RESPONSE ERROR', error, error.response?.statusText)
                    showThrottledErrorToast(notificationStore)
                }

                return Promise.reject(error)
            }
        )

        this._injectionInPlace = true
    }

    // **********************************************************************************************
    // Below is a prototype of an alternative to axios, using fetch-- as there were temporary issues with downloading big files with axios.
    // But the issue seems to have disappeared for unknown reasons -- so sticking with axios for now.
    // **********************************************************************************************
//    protected async fetchWithAuth(url: string, options: RequestInit = {}): Promise<Response> {
//        const sessionStore = useSessionStore()
//        const notificationStore = useNotificationStore()

//        notificationStore.addNotification(NotificationDTO.createForDebug(`REQUEST: ${url}`, 'info'))

//        // Ensure valid token
//        const token = await sessionStore.getValidToken()

//        const headers = new Headers(options.headers || {})

//        if (token) {
//            headers.set('Authorization', token)
//        }

//        const fetchOptions: RequestInit = {
//            ...options,
//            headers,
//        }

//        try {
//            const response = await fetch(url, fetchOptions)
//            DEBUG(`RESPONSE (${url}):`, response)

//            if (!response.ok) {
//                const status = response.status

//                if (status == HttpStatusCode.Unauthorized) {
//                    DEBUG_WARN('RESPONSE ERROR', response.statusText)
//                } else if (status == HttpStatusCode.NotFound) {
//                    DEBUG_WARN('RESPONSE ERROR - UNAUTHORIZED', response.statusText)
//                } else {
//                    DEBUG_ERR_WITHOUT_PROD_NOTIFICATION('RESPONSE ERROR', response.statusText)
//                }

//                throw new Error(`HTTP error! status: ${status}`)
//            }

//            return response
//        } catch (error) {
//            DEBUG_ERR_WITHOUT_PROD_NOTIFICATION('FETCH ERROR', error)
//            throw error
//        }
//    }
}

/********************************************************************************************************************
 AuthenticationApi
 ********************************************************************************************************************/
export class AuthenticationApi extends ApiBase {
    constructor() {
        super('authentication')
    }

    getUserToken(credentials: ICredentialsDTO): Promise<AxiosResponse<ILoginResultDTO>> {
        const url = this.urlBuilder().build('getUserToken')
        return axios.post(url, credentials)
    }

    getDeviceToken(sn: string): Promise<AxiosResponse<ILoginResultDTO>> {
        const url = this.urlBuilder()
            .withParameters({ sn })
            .build('getDeviceToken')

        return axios.get(url)
    }

    requestPasswordReset(email: string): Promise<AxiosResponse> {
        const url = this.urlBuilder()
            .withParameters({ email })
            .build('requestPasswordReset')

        return axios.get(url)
    }

    resetPassword(passwordResetData: IPasswordResetDTO): Promise<AxiosResponse<IPasswordResetResultDTO>> {
        const url = this.urlBuilder()
            .build('resetPassword')

        return axios.post(url, passwordResetData)
    }

    unlockUser(username: string): Promise<AxiosResponse> {
        const url = this.urlBuilder()
            .withParameters({ username })
            .build('unlockUser')

        return axios.get(url)
    }

    refreshToken(accessToken: string, refreshToken: string): Promise<AxiosResponse<ILoginResultDTO>> {
        const url = this.urlBuilder()
            .build('refreshToken')

        return axios.post(url, { accessToken, refreshToken })
    }

    currentDatabase(): Promise<AxiosResponse<string>> {
        const url = this.urlBuilder()
            .build('currentDatabase')

        return axios.get(url)
    }
}

/********************************************************************************************************************
 AccountApi
 ********************************************************************************************************************/
export class AccountApi extends ApiBase {
    constructor() {
        super('account')
    }

    getUserAccountInfo(): Promise<AxiosResponse<IUserAccountInfoDTO>> {
        const url = this.urlBuilder()
            .build('getUserAccountInfo')

        return axios.get(url)
    }

    getPartnerList(): Promise<AxiosResponse<IPartnerAccountFullDTO[]>> {
        const url = this.urlBuilder()
            .build('getPartnerList')

        return axios.get(url)
    }

    setPartnerAdminState(permissionGrants: IPermissionGrant[]): Promise<AxiosResponse<boolean>> {
        const url = this.urlBuilder()
            .build('setPartnerAdminState') 

        return axios.post(url, permissionGrants)
    }

    setUserPriceAccessState(permissionGrants: IPermissionGrant[]): Promise<AxiosResponse<boolean>> {
        const url = this.urlBuilder()
            .build('setUserPriceAccessState')

        return axios.post(url, permissionGrants)
    }

    getSupportedCurrencies(): Promise<AxiosResponse<string[]>> {
        const url = this.urlBuilder()
            .build('getSupportedCurrencies')

        return axios.get(url)
    }

    contactSupport(request: IContactSupportRequestDTO): Promise<AxiosResponse> {
        const url = this.urlBuilder()
            .build('contactSupport')

        return axios.post(url, request)
    }

    requestAccount(request: IRequestAccountDTO): Promise<AxiosResponse> {
        const url = this.urlBuilder()
            .build('requestAccount')

        return axios.post(url, request)
    }
}

/********************************************************************************************************************
 EquipmentApi
 ********************************************************************************************************************/
type TEquipmentBomWarningDict = {
    [key: number]: number
};
export class EquipmentApi extends ApiBase {
    constructor() {
        super('equipment')
    }

    getAllEquipments(): Promise<AxiosResponse<IEquipmentMasterDataWithBomWarningCount[]>> {
        const url = this.urlBuilder()
            .build('getAllEquipmentsExclBomWarningCount')

        return axios.get(url)
    }

    getEquipmentBomWarningCounts(): Promise<AxiosResponse<TEquipmentBomWarningDict>> {
        const url = this.urlBuilder()
            .build('getEquipmentBomWarningCounts')

        return axios.get(url)
    }
   
    getEquipmentBomItemsWithWarnings(equipmentNumber: number): Promise<AxiosResponse<IEquipmentDataWithBomItemWarnings>> {
        const url = this.urlBuilder()
            .withParameters({ equipmentNumber })
            .build('getEquipmentBomItemsWithWarnings')

        return axios.get(url)
    }

    getMyEquipments(): Promise<AxiosResponse<IEquipmentMasterDataWithDocCountDTO[]>> {
        const url = this.urlBuilder()
            .build('getMyEquipments')

        return axios.get(url)
    }

    /**
     * @param serialNo S/N of equipment
     * @returns Master data for the specified equipment
     */
    getMasterData(serialNo: string): Promise<AxiosResponse<IEquipmentMasterDataDTO>> {
        const url = this.urlBuilder()
            .withParameters({ serialNo })
            .build('getMasterData')

        return axios.get(url)
    }

    /**
     * @param equipmentNo Equipment number
     * @returns A complete BOM explosion of the specified equipment
     */
    getExplodedEquipmentBom(equipmentNumber: number, expectedCurrency: string): Promise<AxiosResponse<IBomHeaderDTO>> {
        const url = this.urlBuilder()
            .withParameters({ equipmentNumber, expectedCurrency })
            .build('getExplodedEquipmentBom')

        return axios.get(url)
    }

    /**
     * @param equipmentNo Equipment number
     * @returns Performs a complete BOM explosion of the specified equipment, then returns the branches containing any spare parts
     */
    getSparePartBom(equipmentNumber: number, expectedCurrency: string): Promise<AxiosResponse<IBomHeaderDTO>> {
        const url = this.urlBuilder()
            .withParameters({ equipmentNumber, expectedCurrency })
            .build('getSparePartBom')

        return axios.get(url)
    }

    /**
     * @param equipmentNo Equipment number
     * @returns Performs a complete BOM explosion of the equipment, then
     *          collects all spare-parts (incl. their BOMs) into a flat list.
     */
    getSparePartFlatList(equipmentNumber: number, expectedCurrency: string): Promise<AxiosResponse<IBomHeaderDTO[]>> {
        const url = this.urlBuilder()
            .withParameters({ equipmentNumber, expectedCurrency })
            .build('getSparePartFlatList')

        return axios.get(url)
    }

    serialNoToEquipmentNo(serialNo: string): Promise<AxiosResponse<number>> {
        const url = this.urlBuilder()
            .withParameters({ serialNo })
            .build('serialNoToEquipmentNo')

        return axios.get(url)
    }

    /**
     * @param equipmentNo Equipment number
     * @returns Documents for the specified equipment
     */
    getDocuments(equipmentNumber: number): Promise<AxiosResponse<IDocumentDTO[]>> {
        const url = this.urlBuilder()
            .withParameters({ equipmentNumber })
            .build('getDocuments')

        return axios.get(url)
    }

    /**
     * @param equipmentNo Equipment number
     * @param language Language code
     * @returns Variant configuration items for the specified equipment (with localized texts according to the specified language)
     */
    getVarConfItems(equipmentNumber: number, language: string): Promise<AxiosResponse<IVarConfItemDTO[]>> {
        const url = this.urlBuilder()
            .withParameters({ equipmentNumber, language })
            .build('getVariantConf')

        return axios.get(url)
    }

    /**
     * @param equipmentNo Equipment number
     * @returns Deviations of acc. quantities, between the equipment BOM and the PO BOM, grouped by material number
     */
    getBomDeviations(equipmentNumber: number): Promise<AxiosResponse<IBomDeviationDTO[]>> {
        const url = this.urlBuilder()
            .withParameters({ equipmentNumber })
            .build('getBomDeviations')

        return axios.get(url)
    }

    /**
     * @param equipmentNo Equipment number
     * @returns Dictionary of all spare part material numbers within the BOM explosion of the specified equipment, 
     * EXCLUDING BOM usage 4 during the BOM explosion. The dictionary is indexed by material number, and contains the material qty
     * within the exploded equipment BOM.
     */
    getSparePartNumbers(equipmentNumber: number): Promise<AxiosResponse<NumDictionary<IBomItemQtyDTO>>> {
        const url = this.urlBuilder()
            .withParameters({ equipmentNumber })
            .build('getSparePartNumbers')

        return axios.get(url)
    }

    /**
     * @param buildNumber Build number
     * @returns URL for the download page for the specified build number
     */
    getDownloadUrl(buildNumber: number, serialNo: string): Promise<AxiosResponse<string>> {
        const url = this.urlBuilder()
            .withParameters({ buildNumber, serialNo })
            .build('getDownloadUrl')

        return axios.get(url)
    }
}

/********************************************************************************************************************
 MaterialApi
 ********************************************************************************************************************/
export class MaterialApi extends ApiBase {
    constructor() {
        super('material')
    }

    sparePartExists(materialNumber: number): Promise<AxiosResponse<boolean>> {
        const url = this.urlBuilder()
            .withParameters({ materialNumber })
            .build('sparePartExists')
        return axios.get(url)
    }

    findSparePartByPartnerMaterialNo(partnerMaterialNo: string): Promise<AxiosResponse<number>> {
        const url = this.urlBuilder()
            .withParameters({ partnerMaterialNo })
            .build('findSparePartByPartnerMaterialNo')
        return axios.get(url)
    }

    getData(materialNumbers: number[], expectedCurrency: string): Promise<AxiosResponse<IMaterialDTO[]>> {
        const url = this.urlBuilder()
            .withParameters({ expectedCurrency })
            .build('getData')
        return axios.post(url, materialNumbers)
    }

    allSparePartsWithBoms(expectedCurrency: string): Promise<AxiosResponse<IBomHeaderDTO[]>> {
        const url = this.urlBuilder()
            .withParameters({ expectedCurrency })
            .build('allSparePartsWithBoms')
        return axios.get(url)
    }
}

/********************************************************************************************************************
 BlobApi
 ********************************************************************************************************************/

/** Displays an animated "please wait" message in the specified window, while it waits for its content.
 *  Returns a function, that stops the animation again. */
function showWaitAnimation(targetWindow: Window, localize: (id: string) => string): () => void {
    const pleaseWait = localize('pleaseWait')
    const infoContainer = document.createElement('div')
    let suffix = '...'

    targetWindow.document.title = `${localize('downloadingDocument')}...`
    infoContainer.style.fontSize = '2em'
    infoContainer.textContent = `${pleaseWait}${suffix}`
    targetWindow.document.body.appendChild(infoContainer)

    const timerId = window.setInterval(() => {
        suffix += '.'
        if (suffix.length > 3) suffix = ''
        infoContainer.textContent = `${pleaseWait}${suffix}`
    }, 500)

    return () => window.clearInterval(timerId)
}

export class BlobApi extends ApiBase {
    constructor() {
        super('blob')
    }

    downloadBlob(blobName: string, progressCallback?: (event: AxiosProgressEvent) => void): Promise<AxiosResponse> {
        const url = this.urlBuilder()
            .withSegment('downloadBlob')
            .build(blobName)

        return axios({
            url,
            method: 'GET',
            responseType: 'blob',
            timeout: 120000,
            onDownloadProgress: progressCallback
        })
    }

    /** Creates a short-lived token, granting access to the specified document (blob) only */
    createDocumentToken(blobName: string): Promise<AxiosResponse<string>> {
        const url = this.urlBuilder()
            .withSegment('createDocumentToken')
            .build(blobName)

        return axios.get(url)
    }

    /** Opens the specified document (blob) in a new browser tab.
     *
     *  The tab is navigated to the document URL of the API, so the browser handles the document natively:
     *  it displays the document, or downloads it (with the correct file name), depending on how the user has
     *  configured the browser. The document must NOT be rendered from a blob URL in an iFrame instead: browsers
     *  configured to open PDFs externally (e.g. in Adobe Acrobat) replace such an iFrame with a placeholder,
     *  whose "Open" button cannot act on a blob URL -- leaving the user with a document that cannot be opened.
     *
     *  As the API URL is opened by a browser navigation (which cannot send an "authorization" header), access is
     *  granted by a short-lived, single-document token in the query string instead. */
    async downloadAndShowPdf(blobName: string, localize: (id: string) => string): Promise<void> {
        // Must open the new window immediately, before fetching the token
        // (otherwise the browser might block opening the new window, if the delay is too long)
        const newWindow = window.open('', '_blank')
        const stopWaitAnimation = newWindow != null
            ? showWaitAnimation(newWindow, localize)
            : null

        try {
            const response = await this.createDocumentToken(blobName)
            const documentUrl = this.urlBuilder()
                .withParameter('token', encodeURIComponent(response.data))
                .build('document')

            stopWaitAnimation?.()

            if (newWindow != null) {
                newWindow.location.href = documentUrl
            }
            else {
                // New window was blocked by the browser: download the document from the current tab instead
                const link = document.createElement('a')
                link.href = documentUrl
                link.download = blobName
                document.body.appendChild(link)
                link.click()
                document.body.removeChild(link)
            }
        }
        catch (ex) {
            const errorMsg = `FAILED: ${(ex as AxiosError).message}`

            stopWaitAnimation?.()

            if (newWindow != null) {
                newWindow.document.body.textContent = errorMsg
            }

            DEBUG_ERR_WITHOUT_PROD_NOTIFICATION(errorMsg)
        }
    }

    // **********************************************************************************************
    // Below is a prototype of an alternative to axios, using fetch-- as there were temporary issues with downloading big files with axios.
    // But the issue seems to have disappeared for unknown reasons -- so sticking with axios for now.
    // **********************************************************************************************
    //async downloadBlob(blobName: string, progressCallback?: (event: ProgressEvent) => void): Promise<Blob> {
    //    const url = this.urlBuilder()
    //        .withSegment('downloadBlob')
    //        .build(blobName)

    //    const response = await this.fetchWithAuth(url, {
    //        method: 'GET',
    //        cache: 'no-store',
    //    })

    //    if (progressCallback) {
    //        const reader = response.body?.getReader()
    //        if (reader) {
    //            const contentLength = +response.headers.get('Content-Length')!
    //            let receivedLength = 0
    //            const chunks = []

    //            for (; ;) {
    //                const { done, value } = await reader.read()
    //                if (done) break

    //                chunks.push(value)
    //                receivedLength += value.length

    //                progressCallback({
    //                    loaded: receivedLength,
    //                    total: contentLength,
    //                    lengthComputable: contentLength !== 0,
    //                } as ProgressEvent)
    //            }

    //            const blob = new Blob(chunks, { type: 'application/pdf' })
    //            return blob
    //        }
    //    }

    //    return response.blob()
    //}

    //async downloadAndSaveBlob(blobName: string, loadMessage: string): Promise<void> {
    //    const newWindow = window.open('', '_blank')
    //    const waitMessage = document.createElement('div')

    //    if (newWindow != null) {
    //        this.updateMessage(newWindow, waitMessage, loadMessage)
    //        newWindow.document.body.appendChild(waitMessage)
    //    }

    //    try {
    //        const blob = await this.downloadBlob(blobName, (event) => {
    //            if (event.total) {
    //                const loadedPercentage = Math.round((event.loaded * 100) / event.total)
    //                const message = `${loadMessage} (${loadedPercentage}% Size: ${(event.total / (1024 * 1024)).toFixed(2)} MB)`
    //                this.updateMessage(newWindow, waitMessage, message)
    //            }
    //        })

    //        const url = window.URL.createObjectURL(blob)

    //        if (newWindow != null) {
    //            newWindow.document.body.innerHTML = '' // Clear previous content
    //            newWindow.document.title = blobName // Update the tab title

    //            const objectElement = document.createElement('object')
    //            objectElement.type = 'application/pdf'
    //            objectElement.data = url
    //            objectElement.width = '100%'
    //            objectElement.height = '100%'
    //            newWindow.document.body.appendChild(objectElement)

    //            setTimeout(() => {
    //                window.URL.revokeObjectURL(url)
    //            }, 1000);
    //        } else {
    //            const link = document.createElement('a')
    //            link.href = url
    //            link.download = blobName
    //            document.body.appendChild(link)
    //            link.click()
    //            document.body.removeChild(link)

    //            setTimeout(() => {
    //                window.URL.revokeObjectURL(url)
    //            }, 1000)
    //        }
    //    } catch (error) {
    //        const errorMsg = `FAILED: ${error}`
    //        if (newWindow != null) {
    //            this.updateMessage(newWindow, waitMessage, errorMsg)
    //        }
    //    }
    //}

    //private updateMessage(window: Window | null, container: HTMLDivElement, msg: string) {
    //    if (window != null) {
    //        window.document.title = msg
    //        container.textContent = msg
    //    }
    //}
}

/********************************************************************************************************************
 ClassificationApi
 ********************************************************************************************************************/
export class ClassificationApi extends ApiBase {
    constructor() {
        super('classification')
    }

    get(languageCode: string): Promise<AxiosResponse<PropertyValueDTO[]>> {
        const url = this.urlBuilder().build(languageCode)
        return axios.get(url)
    }
}

/********************************************************************************************************************
 LocalizationApi
 ********************************************************************************************************************/
export class LocalizationApi extends ApiBase {
    constructor() {
        super('localization')
    }

    get(languageCode: string): Promise<AxiosResponse<LocalizationDTO[]>> {
        const url = this.urlBuilder().build(languageCode)
        return axios.get(url)
    }
}

/********************************************************************************************************************
 UnitApi
 ********************************************************************************************************************/
export class UnitApi extends ApiBase {
    constructor() {
        super('unit')
    }

    get(languageCode: string): Promise<AxiosResponse<IUnitDTO[]>> {
        const url = this.urlBuilder().build(languageCode)
        return axios.get(url)
    }
}

/********************************************************************************************************************
 ShoppingCartApi
 ********************************************************************************************************************/
export class ShoppingCartApi extends ApiBase {
    constructor() {
        super('shoppingCart')
    }

    get(): Promise<AxiosResponse<IShoppingCartDTO>> {
        const url = this.urlBuilder().build()
        return axios.get(url)
    }

    reset(): Promise<AxiosResponse<IShoppingCartDTO>> {
        const url = this.urlBuilder().build()
        return axios.delete(url)
    }

    update(materialNumber: number, count: number): Promise<AxiosResponse<IShoppingCartDTO>> {
        const url = this.urlBuilder()
            .withParameter('materialNumber', materialNumber)
            .withParameter('count', count)
            .build()

        return axios.put(url)
    }

    updateSerialNumbers(materialNumber: number, serialNumbers: string[]): Promise<AxiosResponse<IShoppingCartDTO>> {
        const url = this.urlBuilder()
            .build()

        return axios.patch(url, {
            materialNumber,
            serialNumbers
        })
    }

    commit(commitData: IOrderingData): Promise<AxiosResponse<IShoppingCartDTO>> {
        const url = this.urlBuilder()
            .withSegment("commit")
            .build()
        return axios.post(url, commitData)
    }

    sendByEmail(): Promise<AxiosResponse<IShoppingCartDTO>> {
        const url = this.urlBuilder()
            .withSegment("email")
            .build()

        return axios.post(url)
    }

}

/********************************************************************************************************************
OrderProposalApi
********************************************************************************************************************/
export class OrderProposalApi extends ApiBase {
    constructor() {
        super('orderProposal')
    }

    getAll(): Promise<AxiosResponse<IOrderProposalDTO[]>> {
        const url = this.urlBuilder().build()
        return axios.get(url)
    }

    getById(id: string): Promise<AxiosResponse<IOrderProposalDTO>> {
        const url = this.urlBuilder()
            .withSegment(id)
            .build()
        return axios.get(url)
    }

    create(request: ICreateOrderProposalRequest): Promise<AxiosResponse<IOrderProposalDTO>> {
        const url = this.urlBuilder().build()
        return axios.post(url, request)
    }

    delete(id: string): Promise<AxiosResponse<IOrderProposalDTO[]>> {
        const url = this.urlBuilder()
            .withSegment(id)
            .build()
        return axios.delete(url)
    }

    extend(id: string, request: IExtendOrderProposalRequest): Promise<AxiosResponse<IOrderProposalDTO[]>> {
        const url = this.urlBuilder()
            .withSegment(id)
            .withSegment('Extend')
            .build()
        return axios.patch(url, request)
    }

    addToCart(id: string, request: IAddToCartRequest): Promise<AxiosResponse<IShoppingCartDTO>> {
        const url = this.urlBuilder()
            .withSegment(id)
            .withSegment('AddToCart')
            .build()
        return axios.post(url, request)
    }

    sendByEmail(id: string): Promise<AxiosResponse> {
        const url = this.urlBuilder()
            .withSegment(id)
            .withSegment('Email')
            .build()
        return axios.post(url)
    }
}

/********************************************************************************************************************
AddressApi
********************************************************************************************************************/
export class AddressApi extends ApiBase {
    constructor() {
        super('address')
    }

    getDeliveryAddresses(): Promise<AxiosResponse<IDeliveryAddressDTO[]>> {
        const url = this.urlBuilder().build('deliveryAddresses')
        return axios.get(url)
    }

    getCountries(): Promise<AxiosResponse<ICountryDTO[]>> {
        const url = this.urlBuilder().build('countries')
        return axios.get(url)
    }
}

/********************************************************************************************************************
SyncEventApi
********************************************************************************************************************/
export class SyncEventApi extends ApiBase {
    constructor() {
        super('syncEvent')
    }

    getEvents(): Promise<AxiosResponse<ISyncEventDTO[]>> {
        const url = this.urlBuilder().build()
        return axios.get(url)
    }
}

/********************************************************************************************************************
NotificationApi
********************************************************************************************************************/
export class NotificationApi extends ApiBase {
    constructor() {
        super('notification')
    }

    getMessages(): Promise<AxiosResponse<IPortalMessage[]>> {
        const url = this.urlBuilder().build('getPortalMessages')
        return axios.get(url)
    }
}